import { useState } from 'react';

import { BadgeCircle } from '@backrs_ui/common-ui/src/atoms/BadgeCircle';
import { ColorToken } from '@backrs_ui/common-ui/src/designSystem/colors/colors';
import { AppIcon, AppIcons } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { useImageLoad } from '@backrs_ui/common-ui/src/hooks/useImageLoad';
import { HoverButton } from '@backrs_ui/common-ui/src/molecules/Nugget/HoverButton';

import { Box, Skeleton, SxProps, Theme, Typography } from '@mui/material';

import { MeshConfig } from './MeshGradient';
import { NuggetBadge, NuggetBadgeProps } from './NuggetBadge';
import { NuggetGradientFallback } from './NuggetGradientFallback';
import { TypeChip } from '../Nugget/TypeChip';

type FeaturedIconConfig =
  | Readonly<{ icon: AppIcons; bgColor: ColorToken }>
  | Readonly<{ component: 'badgeCircle'; bgColor: ColorToken }>;

export type NuggetCardActionProps = Readonly<{
  hasBookmarked?: boolean;
  onBookmark?: () => void;
  onShare?: () => void;
  isInMyColleges?: boolean;
  onToggleMyColleges?: () => void;
}>;

export type NuggetCardProps = Readonly<{
  imageUrl?: string;
  isVideo?: boolean;
  title: string;
  badge: NuggetBadgeProps;
  entityName: string;
  featuredIcon?: FeaturedIconConfig;
  description?: string;
  onClick?: () => void;
  id?: string;
  actions?: NuggetCardActionProps;
  isHighlighted?: boolean;
  showEntityChip?: boolean;
  meshConfig?: MeshConfig;
  /** When true, the card fills its container width on mobile (xs, <600px); tablet/desktop are unchanged. */
  fluid?: boolean;
}>;

export const NUGGET_CARD_WIDTH = 282;

export const NuggetCard = ({
  imageUrl,
  isVideo,
  title,
  badge,
  entityName,
  featuredIcon,
  description,
  onClick,
  id,
  actions,
  isHighlighted = false,
  showEntityChip = false,
  meshConfig,
  fluid = false,
}: NuggetCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const {
    imageLoaded,
    imageError,
    imageProps: { ref: imageRef, onLoad: handleImageLoad, onError: handleImageError },
  } = useImageLoad(imageUrl);

  const badgeElement = <NuggetBadge {...badge} />;

  const featuredIconElement = featuredIcon ? (
    'component' in featuredIcon ? (
      <FeaturedIconBox bgColor={featuredIcon.bgColor}>
        <BadgeCircle size="lg" />
      </FeaturedIconBox>
    ) : (
      <FeaturedIconBox bgColor={featuredIcon.bgColor}>
        <AppIcon iconName={featuredIcon.icon} diameter="20px" color="Colors/Base/Black" />
      </FeaturedIconBox>
    )
  ) : undefined;

  const hoverButtonElement = actions ? (
    <HoverButton
      isVisible={isHovered}
      idPrefix={id}
      hasBookmarked={actions.hasBookmarked}
      handleActionBookmark={actions.onBookmark}
      handleActionShare={actions.onShare}
      isInMyColleges={actions.isInMyColleges}
      handleActionMyColleges={actions.onToggleMyColleges}
    />
  ) : undefined;

  const showImage = imageUrl && !imageError;

  return (
    <Box
      id={id}
      sx={cardContainerSx(!!onClick, isHighlighted, fluid)}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
    >
      <Box
        sx={imageAreaSx(fluid)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {showImage ? (
          <>
            {!imageLoaded && <Skeleton variant="rectangular" animation="wave" sx={skeletonSx} />}
            {isVideo ? (
              <Box
                component="video"
                src={imageUrl}
                preload="metadata"
                muted
                draggable={false}
                sx={imageWithLoadSx(imageLoaded)}
                onError={handleImageError}
                onLoadedData={handleImageLoad}
              />
            ) : (
              <Box
                component="img"
                src={imageUrl}
                alt=""
                draggable={false}
                ref={imageRef}
                sx={imageWithLoadSx(imageLoaded)}
                onError={handleImageError}
                onLoad={handleImageLoad}
              />
            )}
            {featuredIconElement && <Box sx={featuredIconPositionSx}>{featuredIconElement}</Box>}
            {showEntityChip && (
              <Box sx={entityChipOverlaySx}>
                <TypeChip label={entityName} />
              </Box>
            )}
            {hoverButtonElement}
            <Box sx={badgePositionSx}>{badgeElement}</Box>
          </>
        ) : (
          <NuggetGradientFallback
            entityName={entityName}
            badge={badgeElement}
            featuredIcon={featuredIconElement}
            hoverButton={hoverButtonElement}
            description={description}
            meshConfig={meshConfig}
          />
        )}
      </Box>
      <Box sx={titleRowSx}>
        <Typography variant="text-md-semibold" sx={titleSx}>
          {title}
        </Typography>
        {onClick && (
          <Box className="nugget-card-hover-icon" sx={hoverIconSx}>
            <AppIcon iconName="ArrowUpRight" diameter="20px" color="Colors/Base/Black" />
          </Box>
        )}
      </Box>
    </Box>
  );
};

const FeaturedIconBox = ({
  bgColor,
  children,
}: {
  bgColor: ColorToken;
  children: React.ReactNode;
}) => <Box sx={featuredIconBoxSx(bgColor)}>{children}</Box>;

const featuredIconBoxSx =
  (bgColorToken: ColorToken): SxProps<Theme> =>
  ({ backrs }) => ({
    width: 40,
    height: 40,
    borderRadius: backrs.radius['radius-md'],
    border: `1px solid ${backrs.colors['Colors/Foreground/fg-primary']}`,
    backgroundColor: backrs.colors[bgColorToken],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  });

// fluid cards fill their container on mobile (xs, <600px); tablet/desktop stay fixed-width.
const nuggetMaxWidth = (fluid: boolean) =>
  fluid ? { xs: 'none', sm: NUGGET_CARD_WIDTH } : NUGGET_CARD_WIDTH;

const cardContainerSx =
  (isClickable: boolean, isHighlighted: boolean, fluid: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: backrs.spacing['spacing-md'],
    alignItems: 'flex-start',
    width: '100%',
    maxWidth: nuggetMaxWidth(fluid),
    ...(isClickable && { cursor: 'pointer' }),
    ...(isHighlighted && {
      '& > :first-of-type': {
        outlineColor: backrs.colors['Brand/500'],
        outlineWidth: '3px',
        outlineOffset: '2px',
      },
    }),
    '&:hover .nugget-card-hover-icon': {
      opacity: 1,
    },
  });

const imageAreaSx =
  (fluid: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    maxWidth: nuggetMaxWidth(fluid),
    width: '100%',
    flexShrink: 0,
    aspectRatio: `${NUGGET_CARD_WIDTH} / 180`,
    borderRadius: backrs.radius['radius-2xl'],
    overflow: 'hidden',
    position: 'relative',
    outline: `1px solid ${backrs.colors['Colors/Base/Black']}`,
    transition: 'box-shadow 0.3s ease-in-out, transform 0.3s ease-in-out',
    '&:hover': {
      boxShadow: backrs.shadows['shadow-lg'],
      transform: 'translate(-2px, -2px)',
    },
  });

const imageWithLoadSx = (loaded: boolean): SxProps<Theme> => ({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
  opacity: loaded ? 1 : 0,
  transition: 'opacity 0.3s ease-in-out',
});

const skeletonSx: SxProps<Theme> = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
};

const titleRowSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  paddingLeft: backrs.spacing['spacing-xl'],
  gap: backrs.spacing['spacing-xs'],
});

const titleSx: SxProps<Theme> = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  flex: 1,
  minWidth: 0,
};

const hoverIconSx: SxProps<Theme> = {
  opacity: 0,
  transition: 'opacity 0.2s ease',
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
};

const featuredIconPositionSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  top: backrs.spacing['spacing-xl'],
  left: backrs.spacing['spacing-xl'],
  zIndex: 1,
});

const entityChipOverlaySx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  top: backrs.spacing['spacing-xl'],
  left: backrs.spacing['spacing-xl'],
  maxWidth: 248,
  zIndex: 1,
});

const badgePositionSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  bottom: backrs.spacing['spacing-lg'],
  left: backrs.spacing['spacing-lg'],
});
